import { Button, Label, Radio } from 'flowbite-react';
import React, { useEffect } from 'react';
import { BulkActionByRecruiterInput, UIUserRecommendation } from 'types';
import RecruiterCandidateSendMessage from './RecruiterCandidateSendMessage';
import RecruiterCandidateSendAssignment from './RecruiterCandidateSendAssignment';
import RecruiterCandidateMoveToNextRound from './RecruiterCandidateMoveToNextRound';
import RecruiterShareCandidates from './RecruiterShareCandidate';
import { bulkAction } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import RecruiterCandidateRejectCandidate from './RecruiterCandidateRejectCandidate';
import RecruiterCandidateScreenReject from './RecruiterCandidateScreenReject';
import RecruiterCandidateUndoAutoReject from './RecruiterCandidateUndoAutoReject';
import { get } from 'lodash';
import BriefDebriefModal from './BriefDebriefModal';
import AMAssignBehaviouralFlagToCandidate from './AMAssignBehaviouralFlagToCandidate';
import ScheduleInterview from './schedule-interview/ScheduleInterview';
import CancelInterview from './schedule-interview/CancelInterview';
import RecruiterCandidateSendOnlineTest from './RecruiterCandidateSendOnlineTest';
import RecruiterCandidateSendCaseStudy from './RecruiterCandidateSendCaseStudy';
import RecruiterCandidateUndoReject from './RecruiterCandidateUndoReject';

type Props = {
	bulkSelectedCandidates: UIUserRecommendation[];
	onFinish: () => void;
	positionId?: string;
	defaultBulkAction?: BulkActionByRecruiterInput['action'] | null;
};

const RecruiterCandidatesBulkActionsOnConncetedUsers = ({
	positionId,
	bulkSelectedCandidates,
	onFinish,
	defaultBulkAction = null,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedBulkAction, setSelectedBulkAction] = React.useState<BulkActionByRecruiterInput['action'] | null>();
	const [localActionSelect, setLocalActionSelect] = React.useState<BulkActionByRecruiterInput['action'] | null>();

	useEffect(() => {
		setSelectedBulkAction(defaultBulkAction);
		setLocalActionSelect(defaultBulkAction);
	}, [bulkSelectedCandidates, defaultBulkAction]);

	const bulkActionFn = useMutation(['bulkAction'], bulkAction, {
		onSuccess: async (data: any) => {
			if (data.action === 'download-resumes') {
				const zip = new JSZip();
				for (let resume of data.resumes) {
					zip.file(resume.fileName, resume.content.data);
				}

				const zipBlob = await zip.generateAsync({ type: 'blob' });
				saveAs(zipBlob, 'resumes.zip');
			}

			let toastMessage = 'Request Sent Successfully';
			let toastType: 'success' | 'error' | 'warning' = 'success';

			if (selectedBulkAction === 'message') toastMessage = 'Message sent to Candidates';
			else if (selectedBulkAction === 'send-assignment') toastMessage = 'Assignment sent to Candidates';
			else if (selectedBulkAction === 'send-online-test') toastMessage = 'Online test sent to Candidates';
			else if (selectedBulkAction === 'send-case-study') toastMessage = 'Case study sent to Candidates';
			else if (selectedBulkAction === 'move-to-next') toastMessage = 'Moved selected candidates to their next round';
			else if (selectedBulkAction === 'share-profile') toastMessage = 'Email sent successfully';
			else if (data?.action === 'download-resumes') toastMessage = 'Resumes downloaded successfully';
			else if (data?.action === 'add-behavioural-flag') toastMessage = 'Behavioural flag added successfully';
			else if (selectedBulkAction === 'schedule-interview') toastMessage = 'Interview scheduled successfully';
			else if (selectedBulkAction === 'bulk-cancel-interview') toastMessage = 'Interview cancelled successfully';
			else if (selectedBulkAction === 'bulk-undo-reject' || data?.action === 'bulk-undo-reject') {
				// Handle partial success/failure for bulk undo reject
				if (data.failure > 0 || data.errors?.length > 0) {
					// Check if error contains the limit reached message
					const limitReachedError = data.errors?.find((err: string) =>
						err.includes('Cannot undo rejection. Maximum limit of 2 undo rejections has been reached for this candidate.')
					);

					if (limitReachedError) {
						// Extract the message part (remove application ID prefix if present)
						toastMessage = limitReachedError.includes(': ')
							? limitReachedError.split(': ').slice(1).join(': ')
							: limitReachedError;
						toastType = 'error';
					} else {
						// Show general error handling
						const errorMessages = data.errors?.slice(0, 3).join(', ') || '';
						const errorText = errorMessages.length > 100 ? errorMessages.substring(0, 100) + '...' : errorMessages;
						toastMessage = data.message || `Successfully processed ${data.success || 0} candidates. ${data.failure || 0} failed.${errorText ? ` Errors: ${errorText}` : ''}`;
						toastType = data.success > 0 ? 'warning' : 'error';
					}
				} else {
					toastMessage = 'Candidates added back to pipeline successfully';
				}
			} else if (selectedBulkAction === 'bulk-screen-reject' || data?.action === 'bulk-screen-reject') {
				if (data.failure > 0 || data.errors?.length > 0) {
					const errorMessages = data.errors?.slice(0, 3).join(', ') || '';
					const errorText = errorMessages.length > 100 ? errorMessages.substring(0, 100) + '...' : errorMessages;
					toastMessage = data.message || `Successfully screen rejected ${data.success || 0} candidates. ${data.failure || 0} failed.${errorText ? ` Errors: ${errorText}` : ''}`;
					toastType = data.success > 0 ? 'warning' : 'error';
				} else {
					toastMessage = 'Candidates screen rejected successfully';
				}
				searchParams.set('tab', 'rejected');
				setSearchParams(searchParams);
			} else if (selectedBulkAction === 'bulk-undo-screen-reject' || data?.action === 'bulk-undo-screen-reject') {
				if (data.failure > 0 || data.errors?.length > 0) {
					const errorMessages = data.errors?.slice(0, 3).join(', ') || '';
					const errorText = errorMessages.length > 100 ? errorMessages.substring(0, 100) + '...' : errorMessages;
					toastMessage = data.message || `Successfully processed ${data.success || 0} candidates. ${data.failure || 0} failed.${errorText ? ` Errors: ${errorText}` : ''}`;
					toastType = data.success > 0 ? 'warning' : 'error';
				} else {
					toastMessage = 'Screen rejection undone successfully';
				}
			} else if (selectedBulkAction === 'bulk-undo-auto-reject' || data?.action === 'bulk-undo-auto-reject') {
				// Handle partial success/failure for bulk undo auto reject
				if (data.failure > 0 || data.errors?.length > 0) {
					// Check if error contains the limit reached message
					const limitReachedError = data.errors?.find((err: string) =>
						err.includes('Cannot undo rejection. Maximum limit of 2 undo rejections has been reached for this candidate.')
					);

					if (limitReachedError) {
						// Extract the message part (remove application ID prefix if present)
						toastMessage = limitReachedError.includes(': ')
							? limitReachedError.split(': ').slice(1).join(': ')
							: limitReachedError;
						toastType = 'error';
					} else {
						// Show general error handling
						const errorMessages = data.errors?.slice(0, 3).join(', ') || '';
						const errorText = errorMessages.length > 100 ? errorMessages.substring(0, 100) + '...' : errorMessages;
						toastMessage = data.message || `Successfully processed ${data.success || 0} candidates. ${data.failure || 0} failed.${errorText ? ` Errors: ${errorText}` : ''}`;
						toastType = data.success > 0 ? 'warning' : 'error';
					}
				} else {
					toastMessage = 'Candidates added back to pipeline successfully';
				}
			} else if (data?.message) toastMessage = data?.message;

			const hasPartialFailure =
				Number(data?.failure || 0) > 0 || (Array.isArray(data?.errors) && data.errors.length > 0);
			const isUndoFlow =
				selectedBulkAction === 'bulk-undo-reject' ||
				data?.action === 'bulk-undo-reject' ||
				selectedBulkAction === 'bulk-undo-screen-reject' ||
				data?.action === 'bulk-undo-screen-reject' ||
				selectedBulkAction === 'bulk-undo-auto-reject' ||
				data?.action === 'bulk-undo-auto-reject';
			if (hasPartialFailure && !isUndoFlow) {
				const firstError = Array.isArray(data?.errors) && data.errors.length ? String(data.errors[0]) : '';
				const backendMessage = firstError.includes('message=')
					? firstError.split('message=').slice(1).join('message=')
					: firstError;
				toastType = Number(data?.success || 0) > 0 ? 'warning' : 'error';
				toastMessage = `Success: ${Number(data?.success || 0)} | Failed: ${Number(data?.failure || 0)}${
					backendMessage ? ` | Error: ${backendMessage}` : ''
				}`;
			}

			await queryClient.invalidateQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			await new Promise(resolve => setTimeout(resolve, 100));
			await queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });

			toast({
				title: toastMessage,
				type: toastType,
				duration: toastType !== 'success' ? 6000 : undefined,
			});

			onFinish && onFinish();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			const match = errorMessage.match(/InvalidUserError: (.*)/);
			if (match) {
				toast({
					title: match[1],
					type: 'error',
				});
			} else {
				toast({
					title: errorMessage,
					type: 'error',
				});
			}
		},
	});

	const getDrawerContent = (value: BulkActionByRecruiterInput['action'] | '') => {
		switch (value) {
			case 'schedule-interview': {
				return (
					<ScheduleInterview
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'schedule-interview',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.message,
								interviewLink: data?.interviewLink,
								interviewers: data?.interviewers,
								startTime: data?.startTime,
								duration: data?.duration,
							});
						}}
					/>
				);
			}
			case 'bulk-cancel-interview': {
				return (
					<CancelInterview
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-cancel-interview',
								positionId: data?.positionId,
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.remarks,
							});
						}}
					/>
				);
			}
			case 'message': {
				return (
					<RecruiterCandidateSendMessage
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'message',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.message,
							});
						}}
					/>
				);
			}
			case 'send-assignment': {
				return (
					<>
						<p className="text-xs italic mb-2 text-gray-500">
							Note: People who are already assigned an assignment, will not recive this assignment
						</p>
						<RecruiterCandidateSendAssignment
							positionId={positionId}
							isLoading={bulkActionFn.isLoading}
							onSubmit={data => {
								bulkActionFn.mutate({
									action: selectedBulkAction ?? 'send-assignment',
									positionId: positionId ?? '',
									userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
									assignmentDeadline: data?.assignmentDeadline,
									assignmentDescription: data?.assignmentDescription,
									assignmentLink: data?.assignmentLink,
									assignmentFileLink: data?.assignmentFileLink,
									assignmentSubmissionType: data?.assignmentSubmissionType,
									assignmentLinkOptions: data?.assignmentLinkOptions,
									assignmentFileOptions: data?.assignmentFileOptions,
									assignmentSubmissionNotes: data?.assignmentSubmissionNotes,
								});
							}}
						/>
					</>
				);
			}
			case 'move-to-next': {
				return (
					<RecruiterCandidateMoveToNextRound
						userName="Candidates"
						showMessageInput={false}
						nextRoundLabel="Move to next round"
						positionId={positionId ?? ''}
						currentRound={0}
						totalRounds={0}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'move-to-next',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							});
						}}
					/>
				);
			}
			case 'share-profile': {
				return (
					<RecruiterShareCandidates
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'share-profile',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								emailIds: data?.emailIds,
								message: data?.message,
							});
						}}
					/>
				);
			}
			case 'bulk-reject': {
				return (
					<RecruiterCandidateRejectCandidate
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.remarks,
							});
						}}
					/>
				);
			}
			case 'bulk-screen-reject': {
				return (
					<RecruiterCandidateScreenReject
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-screen-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							});
						}}
					/>
				);
			}
			case 'bulk-undo-auto-reject': {
				return (
					<RecruiterCandidateUndoAutoReject
						userName="Candidates"
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-undo-auto-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							});
						}}
					/>
				);
			}
			case 'send-online-test': {
				return (
					<RecruiterCandidateSendOnlineTest
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'send-online-test',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								onlineTestLink: data?.onlineTestLink,
								prepCoach: data?.prepCoach,
							});
						}}
					/>
				);
			}
			case 'send-case-study': {
				return (
					<>
						<p className="text-xs italic mb-2 text-gray-500">
							Note: People who already have a case study in this round will not receive this case study
						</p>
						<RecruiterCandidateSendCaseStudy
							positionId={positionId}
							isLoading={bulkActionFn.isLoading}
							onSubmit={data => {
								bulkActionFn.mutate({
									action: selectedBulkAction ?? 'send-case-study',
									positionId: positionId ?? '',
									userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
									caseStudyDeadline: data?.caseStudyDeadline,
									caseStudyDescription: data?.caseStudyDescription,
									caseStudyFileLink: data?.caseStudyFileLink,
									caseStudySubmissionType: data?.caseStudySubmissionType,
									caseStudyLinkOptions: data?.caseStudyLinkOptions,
									caseStudyFileOptions: data?.caseStudyFileOptions,
									caseStudySubmissionNotes: data?.caseStudySubmissionNotes,
								});
							}}
						/>
					</>
				);
			}
			case 'download-resumes': {
				return (
					<RecruiterCandidateRejectCandidate
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.remarks,
							});
						}}
					/>
				);
			}

			case 'schedule-brief-debrief-call': {
				return (
					<BriefDebriefModal
						userName="Candidates"
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'schedule-brief-debrief-call',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								message: data?.message,
								callType: data?.callType,
								meetingDateTime: data?.meetingDateTime,
								meetingLink: data?.meetingLink,
								duration: data?.duration,
								additionalEmails: data?.additionalEmails,
								roundNumber: data?.roundNumber,
								roundType: data?.roundType,
								prepCoach: data?.prepCoach,
								label: data?.label,
							});
						}}
					/>
				);
			}

			case 'add-behavioural-flag': {
				return (
					<AMAssignBehaviouralFlagToCandidate
						positionId={positionId ?? ''}
						isLoading={bulkActionFn.isLoading}
						userId={bulkSelectedCandidates[0]?.user?._id ?? ''}
						onSubmitted={flags => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'add-behavioural-flag',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
								flags,
							});
						}}
					/>
				);
			}

			case 'bulk-undo-reject': {
				return (
					<RecruiterCandidateUndoReject
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-undo-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							});
						}}
					/>
				);
			}
			case 'bulk-undo-screen-reject': {
				return (
					<RecruiterCandidateUndoReject
						userName="Candidates"
						positionId={positionId}
						isLoading={bulkActionFn.isLoading}
						onSubmit={data => {
							bulkActionFn.mutate({
								action: selectedBulkAction ?? 'bulk-undo-screen-reject',
								positionId: positionId ?? '',
								userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							});
						}}
					/>
				);
			}

			default:
				return (
					<form
						onSubmit={e => {
							e.preventDefault();
							if (localActionSelect === 'download-resumes' || localActionSelect === 'bulk-undo-reject') {
								if (localActionSelect === 'bulk-undo-reject') {
									bulkActionFn.mutate({
										action: 'bulk-undo-reject',
										userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
										positionId: positionId || '',
									});
								} else {
									bulkActionFn.mutate({
										action: 'download-resumes',
										userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
										positionId: positionId || '',
									});
								}
							} else {
								setSelectedBulkAction(localActionSelect);
							}
						}}
					>
						<div className="mb-3 block">
							<Label htmlFor="action" className="font-[700] font-inter text-sm text-[#28293D]" value="Choose action" />
						</div>

						<fieldset
							className="grid grid-cols-2 max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setLocalActionSelect(target?.value as BulkActionByRecruiterInput['action']);
								}
							}}
						>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'schedule-interview'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'scheduleInterview'}
									name="bulkAction"
									value={'schedule-interview'}
								/>
								<Label htmlFor={'scheduleInterview'} className="text-sm font-[400] font-inter">
									Schedule Interview
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'bulk-cancel-interview'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'cancelInterview'}
									name="bulkAction"
									value={'bulk-cancel-interview'}
								/>
								<Label htmlFor={'cancelInterview'} className="text-sm font-[400] font-inter">
									Cancel Interview
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'message'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'sendMessage'}
									name="bulkAction"
									value={'message'}
								/>
								<Label htmlFor={'sendMessage'} className="text-sm font-[400] font-inter">
									Send message
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'send-assignment'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'sendAssignment'}
									name="bulkAction"
									value={'send-assignment'}
								/>
								<Label htmlFor={'sendAssignment'} className="text-sm font-[400] font-inter">
									Send assignment
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'send-online-test'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'sendOnlineTest'}
									name="bulkAction"
									value={'send-online-test'}
								/>
								<Label htmlFor={'sendOnlineTest'} className="text-sm font-[400] font-inter">
									Send online test
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'send-case-study'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'sendCaseStudy'}
									name="bulkAction"
									value={'send-case-study'}
								/>
								<Label htmlFor={'sendCaseStudy'} className="text-sm font-[400] font-inter">
									Send case study
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'share-profile'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'shareCandidates'}
									name="bulkAction"
									value={'share-profile'}
								/>
								<Label htmlFor={'shareCandidates'} className="text-sm font-[400] font-inter">
									Share Candidates
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'move-to-next'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'moveTo'}
									name="bulkAction"
									value={'move-to-next'}
								/>
								<Label htmlFor={'moveTo'} className="text-sm font-[400] font-inter">
									Move to
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'bulk-screen-reject'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'bulkScreenReject'}
									name="bulkAction"
									value={'bulk-screen-reject'}
								/>
								<Label htmlFor={'bulkScreenReject'} className="text-sm font-[400] font-inter">
									Bulk Screen Reject
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'bulk-reject'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'bulkReject'}
									name="bulkAction"
									value={'bulk-reject'}
								/>
								<Label htmlFor={'bulkReject'} className="text-sm font-[400] font-inter">
									Bulk Reject
								</Label>
							</div>
							{/* <div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'bulk-undo-reject'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'bulkUndoReject'}
									name="bulkAction"
									value={'bulk-undo-reject'}
								/>
								<Label htmlFor={'bulkUndoReject'} className="text-sm font-[400] font-inter">
									Undo Reject
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'bulk-undo-auto-reject'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'bulkUndoAutoReject'}
									name="bulkAction"
									value={'bulk-undo-auto-reject'}
								/>
								<Label htmlFor={'bulkUndoAutoReject'} className="text-sm font-[400] font-inter">
									Undo Auto-Reject
								</Label>
							</div> */}
							{/* {isAccountManagerLoggedIn && (
								<div className="flex items-center gap-2">
									<Radio
										checked={localActionSelect === 'schedule-brief-debrief-call'}
										className="text-[#7777E7] focus:ring-[#7777E7]"
										id={'scheduleBriefDebriefCall'}
										name="scheduleBriefDebriefCall"
										value={'schedule-brief-debrief-call'}
									/>
									<Label htmlFor={'scheduleBriefDebriefCall'} className="text-sm font-[400] font-inter">
										Schedule Brief/Debrief Call
									</Label>
								</div>
							)} */}
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'download-resumes'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'downloadResumes'}
									name="bulkAction"
									value={'download-resumes'}
								/>
								<Label htmlFor={'downloadResumes'} className="text-sm font-[400] font-inter">
									Download Resumes
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Radio
									checked={localActionSelect === 'add-behavioural-flag'}
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={'addBehaviouralFlag'}
									name="bulkAction"
									value={'add-behavioural-flag'}
								/>
								<Label htmlFor={'addBehaviouralFlag'} className="text-sm font-[400] font-inter">
									Add Behavioural Flag
								</Label>
							</div>
						</fieldset>
						<Button
							isProcessing={(localActionSelect === 'download-resumes' || localActionSelect === 'bulk-undo-reject') && bulkActionFn.isLoading}
							type="submit"
							color="purplePlaced"
							className="bg-[#7777E7] w-full mt-[24px]"
							disabled={!localActionSelect}
						>
							{localActionSelect === 'download-resumes' ? 'Download' : localActionSelect === 'bulk-undo-reject' ? 'Confirm' : 'Select'}
						</Button>
					</form>
				);
		}
	};

	return <>{getDrawerContent(selectedBulkAction ?? '')}</>;
};

export default RecruiterCandidatesBulkActionsOnConncetedUsers;
